import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Col, FormGroup, Row, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import LoaderButton from '../loader-button/LoaderButton';
import CredsModal from '../managed-product/Vendor/CredsModal';

// services
import BZAPIService from '../../services/BZAPIService';
//libs
import { useFormFields } from '../../libs/hooksLib';
import { FieldGroup } from '../../libs/formLib';
import { adminIsReadOnlyCheck } from '../../helpers/rbac';
// constants
import SETTINGS from '../../constants/settings';
import Heading from '../common/Heading';
import Loader from '../common/Loader';
import ButtonIcon from '../common/ButtonIcon';

const SplunkSettings = () => {
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fields, handleFieldChange] = useFormFields({
    splunkAccessToken: '',
    secretId: '',
    splunkEndpoint: '',
    enabled: false
  });
  const [enableProviderData, setenableProviderData] = useState(true);
  const [isAddingCredentials, setIsAddingCredentials] = useState(false);
  const [isValidCredentials, setIsValidCredentials] = useState(false);
  const [isTestingValidCredentials, setIsTestingValidCredentials] = useState(false);

  const navigate = useNavigate();
  useEffect(() => {
    async function onLoad() {
      try {
        // Load permissions first so a failure in an unrelated call below can't leave the page stuck as read-only
        const myUserPermissions = await BZAPIService.getMyUserPermissions();
        setIsReadOnly(adminIsReadOnlyCheck(myUserPermissions));

        let settings = await BZAPIService.getSetting(SETTINGS.SPLUNK);

        if (!settings)
          settings = {
            splunkAccessToken: '',
            secretId: '',
            splunkEndpoint: '',
            enabled: false
          };
        fields.secretId = settings.secretId;
        fields.splunkEndpoint = settings.splunkEndpoint;
        fields.enabled = settings.enabled;
        setenableProviderData(settings.enabled ? true : false);
        setIsLoading(false);
      } catch (e) {
        console.log(e);
        setIsLoading(false);
      }
    }

    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.secretId]);
  async function testCreds() {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setIsTestingValidCredentials(true);
    const data = {
      splunkAccessToken: fields.splunkAccessToken,
      splunkEndpoint: fields.splunkEndpoint
    };
    const result = await BZAPIService.integrationSplunkTest(data);
    setIsTestingValidCredentials(false);
    if (result) {
      // valid creds
      if (!result.valid) {
        return toast.error(`Unable to validate Splunk credentials.`);
      }
      setIsValidCredentials(true);
      setIsAddingCredentials(false);
      toast.success(`Splunk credentials validated.`);
    } else {
      toast.error(`Unable to validate Splunk credentials.`);
    }

    console.log('result', result);
  }

  async function saveAWSSecret() {
    const data = {
      secret: {
        splunkAccessToken: fields.splunkAccessToken
      },
      vendorId: 'splunk',
      key: 'splunkAccessToken'
    };
    // service api secret
    return BZAPIService.createOrUpdateAWSSecret(data);
  }

  async function saveConfig(secret) {
    const data = {
      type: 'global',
      enabled: enableProviderData,
      secretId: secret.Name,
      splunkEndpoint: fields.splunkEndpoint
    };
    const syncOnly = false;

    return BZAPIService.saveSetting(SETTINGS.SPLUNK, syncOnly, data);
  }
  async function handleSave() {
    try {
      // validate url
      let validUrl;
      try {
        const parsed = new URL(fields.splunkEndpoint);
        validUrl = parsed.protocol === 'http:' || parsed.protocol === 'https:' ? [fields.splunkEndpoint] : null;
      } catch {
        validUrl = null;
      }
      console.log('validUrl', validUrl);
      if (validUrl) setIsValidUrl(true);
      setIsSubmitted(true);
      setIsLoading(true);
      // Validate
      if (!fields.splunkEndpoint || !validUrl) {
        setIsLoading(false);

        return;
      }
      // Validate Secrets
      if (!fields.secretId) {
        if (!fields.splunkAccessToken) {
          console.log('misisng secret');
          setIsLoading(false);
          return;
        }
      }
      let secret;

      if (fields.splunkAccessToken) {
        // Updated secrets
        secret = await saveAWSSecret();
      } else {
        // use existing secrets
        secret = { Name: fields.secretId };
      }
      // console.log('secret', secret);
      if (!secret || !secret.Name) {
        setIsLoading(false);
        throw Error('Missing Secret');
      }

      await saveConfig(secret);
      setIsLoading(false);
      setIsSubmitted(false);
      toast.success(`Settings Saved!`);
    } catch (e) {
      setIsLoading(false);
      toast.error('Failed to save.');
      console.log('e', e);
    }
    // Go back to listing
  }

  const handleeEnableProviderDataChange = e => {
    const enableProviderDataValue = e.target.checked;
    console.log('enableProviderDataValue', enableProviderDataValue);
    enableProviderDataValue === true ? setenableProviderData(true) : setenableProviderData(false);
  };

  if (isLoading && !isSubmitted) return <Loader text="Loading Splunk settings..." />;
  return (
    <>
      <Heading
        title="Splunk Configuration"
        subtitle="Specify your Corporate Splunk configuration"
        className="mb-4 mt-3"
        icon="cog"
      />
      {isReadOnly && (
        <Alert color="info">Note: Your user only has read-only access. You will not be able to save any updates.</Alert>
      )}

      <Card>
        <CardBody className="fs--1">
          <div className="d-flex align-items-center mb-4">
            <input
              type="checkbox"
              id="exampleCustomCheckbox"
              label="Enable"
              checked={enableProviderData}
              onChange={e => handleeEnableProviderDataChange(e)}
              data-testid={'enable-provider'}
            />
            <label className="ml-2 mb-0">Enable</label>
          </div>
          <FieldGroup
            type="text"
            label="Splunk Endpoint URL"
            id="splunkEndpoint"
            onChange={handleFieldChange}
            value={fields.splunkEndpoint}
            placeholder="Enter Splunk Endpoint URL"
            // style={{ marginBottom: '10px' }}
            help="Eg. https://http-inputs.<your_cloud_id>.splunkcloud.com/services/collector/event"
            required
            data-testid={'splunk-endpoint-url'}
            feedback="Please specify a valid Splunk Endpoint URL."
            showFeedback={!isValidUrl && isSubmitted}
            // className="mt-2"
          />
          <FormGroup>
            {!isAddingCredentials ? (
              <div className="d-flex align-items-center">
                <ButtonIcon
                  onClick={() => setIsAddingCredentials(true)}
                  color="falcon-default"
                  size="sm"
                  disabled={!fields.splunkEndpoint}
                  icon="plus"
                  iconClassName="fs--2"
                  className="mr-2"
                  data-testid={'sn-set-credentials-btn'}
                >
                  Set Credentials
                </ButtonIcon>
                {fields.secretId || isValidCredentials ? (
                  <div className="d-flex align-items-center" data-testid={'sn-creds-stored-message'}>
                    <FontAwesomeIcon icon={'check'} style={{ color: 'green' }} className="mr-1" />
                    Splunk API Credentials Stored
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <Row>
                  <Col lg="12">
                    <FieldGroup
                      type="text"
                      label="Splunk Credentials"
                      id="splunkAccessToken"
                      data-testid={'splunk-access-token'}
                      onChange={handleFieldChange}
                      value={fields.splunkAccessToken}
                      placeholder="Enter access token"
                      style={{ marginBottom: '10px' }}
                    />
                  </Col>
                </Row>
                <br />
                <CredsModal
                  isTestingValidCredentials={isTestingValidCredentials}
                  setIsTestingCredentials={setIsTestingValidCredentials}
                  fields={fields}
                  testCreds={testCreds}
                  vendorId={'splunk'}
                />
                <ButtonIcon
                  onClick={() => setIsAddingCredentials(false)}
                  color="falcon-default"
                  data-testid={'sn-cancel-creds'}
                  size="sm"
                  icon="times"
                  iconClassName="fs--2"
                  style={{ marginRight: '10px' }}
                >
                  Cancel
                </ButtonIcon>
              </>
            )}
          </FormGroup>
          {/* </FormGroup> */}

          {/* <FormGroup lg="5"> */}
          {/* </FormGroup> */}
        </CardBody>
      </Card>
      <Row className="flex-between-center" style={{ marginTop: '40px' }}>
        <Col xs="auto">
          <Button
            block
            color="falcon-danger"
            onClick={() => {
              navigate(-1);
            }}
          >
            Cancel
          </Button>
        </Col>
        <Col xs="auto">
          <LoaderButton
            block
            color="falcon-primary"
            onClick={handleSave}
            name="save-and-add"
            isLoading={isLoading}
            data-testid={'save-btn'}
            disabled={isReadOnly}
          >
            Save changes
          </LoaderButton>
        </Col>
      </Row>
    </>
  );
};

export default SplunkSettings;
